<template>
  <dp-seite titel="Besetzung & Abdeckung" untertitel="Genau eine Wochenquelle je Mitarbeiter und ISO-Woche" :snack="snack">
    <template #aktionen>
      <v-btn small depressed @click="$router.push({path: '/dienstplanung/zuordnung', query: {typ: 'MACHINE', woche: woche, schritt: 4}})"><v-icon small left>mdi-truck</v-icon>Fahrer einteilen</v-btn>
      <v-btn small depressed color="success" :disabled="!dirty || !settings.can_edit" @click="$refs.speichern.open()">
        <v-icon small left>mdi-content-save</v-icon>Entwurf speichern<span v-if="dirty">&nbsp;({{ dirty }})</span>
      </v-btn>
    </template>
    <template v-if="dp.plan">
      <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
        <v-select v-model="woche" :items="wochenItems" outlined dense hide-details label="Kalenderwoche" style="max-width: 280px"/>
        <v-btn icon small :disabled="idx <= 0" title="Vorherige Woche" @click="woche = wochen[idx - 1].key"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <v-btn icon small :disabled="idx >= wochen.length - 1" title="Nächste Woche" @click="woche = wochen[idx + 1].key"><v-icon>mdi-chevron-right</v-icon></v-btn>
        <v-chip small outlined :color="probleme.length || fehlerWochen ? 'orange darken-2' : 'success'">
          {{ fehlerWochen }} Mitarbeiter mit Hinweisen · {{ probleme.length }} unbesetzte Maschinentage
        </v-chip>
        <v-spacer/>
        <v-text-field v-model="suche" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" label="Mitarbeiter suchen" style="max-width: 240px"/>
        <v-btn-toggle v-model="filter" dense mandatory color="primary">
          <v-btn small value="alle">Alle</v-btn>
          <v-btn small value="hinweise">Mit Hinweisen</v-btn>
          <v-btn small value="ohne">Ohne Quelle</v-btn>
        </v-btn-toggle>
      </div>
      <v-alert v-if="dirty" type="info" dense text class="mb-2">Stunden und Hinweise prüft der Server für den <b>gespeicherten</b> Entwurf. Vorgemerkte Änderungen erscheinen nach dem Speichern.</v-alert>

      <v-simple-table dense class="dp-table">
        <thead>
        <tr><th>Mitarbeiter</th><th>Pools</th><th>Wochenquelle</th><th>Dienst</th><th>Soll / Woche</th><th>Zeitkontrolle</th><th>Hinweise</th><th></th></tr>
        </thead>
        <tbody>
        <tr v-for="r in zeilen" :key="r.mita">
          <td><b>{{ name(r.mita) }}</b><v-chip v-if="isStagedB(r.mita, woche)" x-small color="success" dark class="ml-1">vorgemerkt</v-chip><div class="text-caption grey--text">{{ r.pers }}</div></td>
          <td class="text-caption">{{ r.pools }}</td>
          <td><v-chip x-small outlined :color="r.quelle ? 'primary' : 'orange darken-2'">{{ r.quelle ? quellen[r.quelle.typ] : 'keine' }}</v-chip></td>
          <td class="text-caption">{{ r.quelle ? quelleLabel(r.quelle) : (r.lueckeMoeglich ? 'nicht eingeplant' : 'Standardraster (Stammdaten)') }}</td>
          <td>{{ r.analyse ? hhmm(r.analyse.stunden) : '–' }}</td>
          <td><v-chip v-if="r.analyse" x-small :color="tkStatus(r.analyse).color" outlined>{{ tkStatus(r.analyse).text }}</v-chip></td>
          <td class="text-caption">
            <div v-for="f in (r.analyse ? r.analyse.fehler : [])" :key="f" class="red--text">{{ errorCodes[f] || f }}</div>
          </td>
          <td class="text-no-wrap">
            <v-menu offset-y left>
              <template v-slot:activator="{ on }"><v-btn x-small depressed v-on="on" :disabled="!settings.can_edit">Quelle<v-icon small right>mdi-menu-down</v-icon></v-btn></template>
              <v-list dense>
                <v-list-item v-for="k in kolonnenFuer(r.mita)" :key="k.id" @click="setzen(r, {typ: 'CREW', planungsgruppe_id: k.id})">
                  <v-list-item-title>Kolonne: {{ k.bezeichnung }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push({path: '/dienstplanung/zuordnung', query: {typ: 'DIRECT', ziel: r.mita, woche, schritt: 2}})"><v-list-item-title>Direkte Musterwoche …</v-list-item-title></v-list-item>
                <v-list-item @click="setzen(r, {typ: 'STANDARD'})"><v-list-item-title>Standardraster (Stammdaten)</v-list-item-title></v-list-item>
                <v-list-item @click="vertretungNeu(r.mita)"><v-list-item-title>Tagesvertretung …</v-list-item-title></v-list-item>
                <v-divider/>
                <v-list-item :disabled="!r.quelle" @click="setzen(r, null)"><v-list-item-title class="red--text">Wochenquelle entfernen</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
            <v-btn x-small text color="primary" :disabled="!r.analyse" @click="tage(r)">Tage</v-btn>
          </td>
        </tr>
        <tr v-if="!zeilen.length"><td colspan="8" class="grey--text">Keine Mitarbeiter für diese Auswahl.</td></tr>
        </tbody>
      </v-simple-table>

      <div v-if="probleme.length" class="mt-4">
        <h3 class="text-subtitle-2 mb-1">Unbesetzte Maschinentage</h3>
        <div v-for="(p, i) in probleme" :key="i" class="text-body-2">
          <v-icon small color="orange darken-2">mdi-alert</v-icon>
          {{ fmtDay(p.datum) }} · {{ gruppeName(p.planungsgruppe_id) }} · {{ (position(p.fahrerposition_id) || {}).bezeichnung }} unbesetzt
          <v-btn x-small text color="primary" @click="$router.push({path: '/dienstplanung/zuordnung', query: {typ: 'MACHINE', ziel: p.planungsgruppe_id, woche, schritt: 4}})">Fahrer einteilen</v-btn>
        </div>
      </div>

      <div class="mt-5">
        <div class="d-flex align-center mb-1">
          <h3 class="text-subtitle-2">Tagesvertretungen in KW {{ info ? info.nummer : '' }}</h3>
          <v-spacer/>
          <v-btn small text color="primary" :disabled="!settings.can_edit" @click="vertretungNeu('')"><v-icon small left>mdi-account-switch</v-icon>Vertretung hinzufügen</v-btn>
        </div>
        <v-simple-table dense class="dp-table">
          <thead><tr><th>Datum</th><th>Vertretung</th><th>vertritt</th><th>Bemerkung</th><th></th></tr></thead>
          <tbody>
          <tr v-for="v in vertretungen" :key="v.mita_bis_id + v.datum">
            <td>{{ fmtDay(v.datum) }}</td>
            <td>{{ name(v.mita_bis_id) }}<v-chip v-if="v.vorher !== undefined" x-small color="success" dark class="ml-1">vorgemerkt</v-chip></td>
            <td>{{ name(v.vertretener_bis_id) }}</td>
            <td class="text-caption">{{ v.bemerkung }}</td>
            <td><v-btn x-small text color="error" :disabled="!settings.can_edit" @click="stageVertretung(v.mita_bis_id, v.datum, null)">Entfernen</v-btn></td>
          </tr>
          <tr v-if="!vertretungen.length"><td colspan="5" class="grey--text">Keine Tagesvertretungen.</td></tr>
          </tbody>
        </v-simple-table>
        <div class="text-caption grey--text mt-1">Die Vertretung arbeitet am Tag den geplanten Dienst der vertretenen Person (Vorrang vor der eigenen Wochenquelle).
          Stammraster, Abwesenheiten, Ist-Zeiten und Buchungen beider Personen bleiben unverändert.</div>
      </div>
      <div class="info-box mt-4"><v-icon small>mdi-lock-outline</v-icon> Ersetzen ist immer ausdrücklich: Die bisherige Quelle wird nur für diese Woche entfernt.
        Pool-Mitgliedschaften, Jahresraster, Ist-Zeiten und veröffentlichte Stände ändern sich nicht.</div>
    </template>

    <bestaetigen-dialog ref="ersetzen" titel="Wochenquelle ändern" ok-text="Vormerken" :pflicht-text="ersetzenText" @ok="setzenBestaetigt">
      <p class="text-body-2" v-if="pending"><b>{{ name(pending.r.mita) }}</b> · KW {{ info.nummer }}<br>
        bisher: {{ quelleLabel(pending.r.quelle) }}<br>neu: {{ pending.quelle ? neuLabel(pending.quelle) : 'keine Wochenquelle' }}</p>
    </bestaetigen-dialog>
    <bestaetigen-dialog ref="speichern" titel="Entwurf speichern" ok-text="Speichern" ok-farbe="success" breit :busy="busy" @ok="speichern">
      <aenderungen-tabelle :rows="rows"/>
    </bestaetigen-dialog>
    <bestaetigen-dialog ref="tage" :titel="tageTitel" breit>
      <v-simple-table dense>
        <thead><tr><th>Tag</th><th>Quelle</th><th>Soll</th><th>Arbeitszeit</th><th>Pausen</th><th>Schicht</th><th>Zeitkontrolle</th></tr></thead>
        <tbody>
        <tr v-for="t in tageZeilen" :key="t.datum">
          <td>{{ fmtDay(t.datum) }}<v-chip v-if="t.feiertag" x-small color="red lighten-4" class="ml-1">{{ t.feiertag }}</v-chip></td>
          <td>{{ sources[t.quelle] || '–' }}</td>
          <td>{{ hhmm(t.ziel.zeit_soll) }}</td>
          <td>{{ t.ziel.soll_zeit }}</td>
          <td class="text-caption">{{ [t.ziel.pause_1, t.ziel.pause_2].filter(p => p && p !== '00:00-00:00').join(', ') || '–' }}</td>
          <td>{{ t.ziel.schicht }}</td>
          <td><v-chip x-small :color="(actions[t.aktion] || {}).color" dark>{{ (actions[t.aktion] || {}).text }}</v-chip>
            <span v-if="t.fehler" class="red--text text-caption ml-1">{{ errorCodes[t.fehler] || t.fehler }}</span></td>
        </tr>
        </tbody>
      </v-simple-table>
    </bestaetigen-dialog>
    <v-dialog ref="vdlg" v-model="vDialog" max-width="560">
      <v-card>
        <v-card-title class="text-h6">Tagesvertretung</v-card-title>
        <v-card-text>
          <v-autocomplete v-model="vForm.mita" :items="mitaItems" outlined dense label="Vertretung (arbeitet den Dienst)" class="mt-2"/>
          <v-select v-model="vForm.datum" :items="tagItems" outlined dense label="Tag"/>
          <v-autocomplete v-model="vForm.vertreten" :items="geplanteItems" outlined dense label="vertritt" hint="Nur Mitarbeiter mit Wochenquelle in dieser Woche" persistent-hint/>
          <v-text-field v-model="vForm.bemerkung" outlined dense label="Bemerkung (z. B. Grund)" maxlength="255" class="mt-3"/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="vDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed :disabled="!vForm.mita || !vForm.datum || !vForm.vertreten || vForm.mita === vForm.vertreten" @click="vertretungVormerken">Vormerken</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </dp-seite>
</template>

<script>
import {DateTime} from 'luxon'
import {ACTIONS, dpApi, ERROR_CODES, errorText, fmtDay, fmtHHMM, periodWeeks, QUELLEN, SOURCES, weekInfo, weekLabel} from '@/shared/dienstplan_api'
import seite from './seite'
import {changeRows, dirtyCount, dp, effBesetzung, effVertretung, gruppeName, inPool, isStagedB, kolonnen, maschinen, name, position, quelleLabel,
  saveDraft, savedVertretung, stageBesetzung, stageVertretung} from './store'
import DpSeite from './components/DpSeite'
import BestaetigenDialog from './components/BestaetigenDialog'
import AenderungenTabelle from './components/AenderungenTabelle'
import raiseStack from './components/raiseStack'

export default {
  name: 'DpBesetzung',
  mixins: [seite, raiseStack],
  components: {DpSeite, BestaetigenDialog, AenderungenTabelle},
  data: () => ({
    woche: '', suche: '', filter: 'alle', analyse: null, busy: false, pending: null,
    tageTitel: '', tageZeilen: [], vDialog: false, vForm: {mita: '', datum: '', vertreten: '', bemerkung: ''},
    quellen: QUELLEN, sources: SOURCES, actions: ACTIONS, errorCodes: ERROR_CODES,
  }),
  computed: {
    dirty: () => dirtyCount(),
    rows: () => changeRows(),
    wochen () {
      return periodWeeks(dp.range.von, dp.range.bis)
    },
    wochenItems () {
      return this.wochen.map(w => ({value: w.key, text: weekLabel(w)}))
    },
    idx () {
      return this.wochen.findIndex(w => w.key === this.woche)
    },
    info () {
      return weekInfo(this.woche)
    },
    analyseMap () {
      const m = {}
      if (this.analyse) this.analyse.mitarbeiter_wochen.filter(x => x.iso_woche === this.woche).forEach(x => { m[x.mita_bis_id] = x })
      return m
    },
    probleme () {
      return this.analyse ? this.analyse.probleme.filter(p => p.iso_woche === this.woche && p.code === 'POSITION_VACANT') : []
    },
    fehlerWochen () {
      return Object.values(this.analyseMap).filter(x => x.fehler.length).length
    },
    // planned population: employees with a weekly source in the range, pool members and analysed employees
    alleZeilen () {
      if (!this.woche) return []
      const ids = new Set()
      dp.plan.besetzungen.forEach(b => ids.add(b.mita_bis_id))
      Object.values(dp.staged.besetzungen).forEach(b => ids.add(b.mita_bis_id))
      kolonnen().concat(maschinen()).forEach(g => g.mitglieder.forEach(m => ids.add(m.mita_bis_id)))
      Object.keys(this.analyseMap).forEach(id => ids.add(id))
      const planned = new Set(dp.plan.besetzungen.map(b => b.mita_bis_id))
      return dp.basis.mitarbeiter.filter(m => ids.has(m.bis_id)).map(m => ({
        mita: m.bis_id,
        pers: m.pers_number,
        quelle: effBesetzung(m.bis_id, this.woche),
        analyse: this.analyseMap[m.bis_id],
        lueckeMoeglich: planned.has(m.bis_id),
        pools: kolonnen().concat(maschinen()).filter(g => g.mitglieder.some(x => x.mita_bis_id === m.bis_id)).map(g => g.bezeichnung).join(', '),
      })).sort((a, b) => name(a.mita).localeCompare(name(b.mita)))
    },
    zeilen () {
      const s = (this.suche || '').toLowerCase()
      return this.alleZeilen.filter(r => (!s || name(r.mita).toLowerCase().includes(s) || r.pers.includes(s)) &&
        (this.filter === 'alle' || (this.filter === 'ohne' ? !r.quelle : r.analyse && r.analyse.fehler.length)))
    },
    vertretungen () {
      if (!this.info) return []
      const res = dp.plan.vertretungen.filter(v => v.datum >= this.info.montag && v.datum <= this.info.sonntag && effVertretung(v.mita_bis_id, v.datum))
        .map(v => effVertretung(v.mita_bis_id, v.datum))
      Object.values(dp.staged.vertretungen).filter(v => !v.loeschen && v.datum >= this.info.montag && v.datum <= this.info.sonntag && !savedVertretung(v.mita_bis_id, v.datum))
        .forEach(v => res.push(v))
      return res.sort((a, b) => (a.datum + a.mita_bis_id).localeCompare(b.datum + b.mita_bis_id))
    },
    ersetzenText () {
      return this.pending && this.pending.r.quelle ? 'Die bisherige Wochenquelle wird nur für diese Woche ersetzt bzw. entfernt.' : ''
    },
    mitaItems () {
      return dp.basis.mitarbeiter.map(m => ({value: m.bis_id, text: name(m.bis_id) + ' (' + m.pers_number + ')'}))
    },
    geplanteItems () {
      return this.alleZeilen.filter(r => r.quelle && r.quelle.typ !== 'STANDARD' && r.mita !== this.vForm.mita).map(r => ({value: r.mita, text: name(r.mita) + ' – ' + quelleLabel(r.quelle)}))
    },
    tagItems () {
      if (!this.info) return []
      return [0, 1, 2, 3, 4, 5, 6].map(i => DateTime.fromISO(this.info.montag).plus({days: i})).map(d => ({value: d.toISODate(), text: d.setLocale('de').toFormat('cccc, dd.MM.yyyy')}))
    },
  },
  watch: {
    vDialog (v) {
      if (v) {
        this.raiseStack('vdlg')
      }
    },
    wochen: {
      immediate: true,
      handler (ws) {
        if (ws.length && !ws.some(w => w.key === this.woche)) this.woche = ws[0].key
      },
    },
    woche () {
      this.analysieren()
    },
  },
  created () {
    this.ensure().then(() => {
      const q = this.$route.query.woche
      if (q && weekInfo(q)) {
        if (!this.wochen.some(w => w.key === q)) {
          const w = weekInfo(q)
          this.reloadPlan({von: w.montag, bis: DateTime.fromISO(w.montag).plus({weeks: 4, days: 6}).toISODate()}).then(() => { this.woche = q })
        } else this.woche = q
      }
      this.analysieren()
    })
  },
  methods: {
    name,
    gruppeName,
    position,
    quelleLabel,
    isStagedB,
    stageVertretung,
    fmtDay,
    hhmm: fmtHHMM,
    analysieren () {
      if (!this.info || !dp.plan) return
      dpApi.analyse(this.info.montag, this.info.sonntag).then(res => { this.analyse = res }, res => this.fail(res))
    },
    tkStatus (a) {
      if (a.fehler.length) return {text: 'nicht veröffentlichbar', color: 'error'}
      if (a.aktionen.INSERT || a.aktionen.UPDATE) return {text: 'Änderung offen', color: 'primary'}
      if (a.aktionen.LOCKED) return {text: 'teils bestätigt', color: 'orange darken-2'}
      return {text: 'aktuell', color: 'success'}
    },
    kolonnenFuer (mita) {
      return kolonnen().filter(k => inPool(k.id, mita, this.woche))
    },
    neuLabel (q) {
      return q.typ === 'CREW' ? 'Kolonne ' + gruppeName(q.planungsgruppe_id) : QUELLEN[q.typ]
    },
    setzen (r, quelle) {
      this.pending = {r, quelle}
      this.$refs.ersetzen.open()
    },
    setzenBestaetigt () {
      stageBesetzung(this.pending.r.mita, this.woche, this.pending.quelle)
      this.$refs.ersetzen.close()
      this.toast('Änderung vorgemerkt. Speichern nicht vergessen.')
    },
    tage (r) {
      this.tageTitel = name(r.mita) + ' · ' + weekLabel(this.info)
      this.tageZeilen = r.analyse.tage
      this.$refs.tage.open()
    },
    vertretungNeu (mita) {
      this.vForm = {mita, datum: this.info.montag, vertreten: '', bemerkung: ''}
      this.vDialog = true
    },
    vertretungVormerken () {
      const f = this.vForm
      stageVertretung(f.mita, f.datum, {vertretener_bis_id: f.vertreten, bemerkung: f.bemerkung})
      this.vDialog = false
      this.toast('Tagesvertretung vorgemerkt.')
    },
    speichern () {
      this.busy = true
      saveDraft('Besetzung ' + weekLabel(this.info)).then(() => {
        this.busy = false
        this.$refs.speichern.close()
        this.toast('Entwurf gespeichert.')
        this.analysieren()
      }, res => {
        this.busy = false
        this.toast(errorText(res), 'red accent-2')
      })
    },
  },
}
</script>

<style scoped>
.info-box {
  padding: 10px 14px;
  background: #f5f8fb;
  border-left: 3px solid #87a6c4;
  font-size: 12px;
  color: #50647a;
}
</style>
